import React from "react";
import {
  Alert,
  Card,
  Col,
  Row,
  Button,
  Badge,
  Form,
} from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Table from "@enerex/unified-ui-react/Table";
import Div from "../../../components/Div";
import PageTitle from "../../../components/PageTitle/index";
import { useEffect, useState, useCallback, useRef } from "react";
import ThreeDotMenu from "../../../components/ThreeDotMenu";
import DropdownItem from "react-bootstrap/DropdownItem";
import Select from "components/UnifiedSelect";
import AsyncSelect from "components/UnifiedAsyncSelect";
import FormInput from "components/FormInput/index";
import BlurSpan from "components/BlurSpan";
import { sortByProperty, filterRecords } from "./../../../helpers/api/utils";
import ConfirmButton from "../../../components/ConfirmButton";
import { useAuth } from "providers/CustomAuthProvider";
import PlusIcon from '@enerex/unified-ui-react/icons/AddIcon';
import { DEV_URL, rpost } from "../../../utils/requests";
import useShowSnackbar from "hooks/useShowSnackbar";
import AddChildModal from "../modals/AddChildModal";
import { updateCompanyHierarchy } from "../api/hierarchy";
import loader from "assets/images/datahub-loader.svg";
import { Link } from "react-router-dom";
import { BrandsData } from "./BrandsService";
import useGlobalPremiumFlag from "hooks/useGlobalPremiumFlag";

const COMPANY_PAGE_SIZE = 30;

const ALIAS_TYPE_OPTIONS = [
  { value: "FKA", label: "FKA" },
  { value: "DBA", label: "DBA" },
];

const findOptionByValue = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const getAliasTypeBadgeVariant = (aliasType) => {
  switch (aliasType) {
    case "FKA":
      return "warning";
    case "MERGE":
    case "Brand":
      return "success";
    default:
      return "info";
  }
};

const getAliasTypeTitle = (aliasType) => {
  switch (aliasType) {
    case "FKA":
      return "For Known As";
    case "DBA":
      return "Doing Business As";
    case "Brand":
      return "Brand";
    default:
      return aliasType;
  }
};

const isBrandAlias = (aliasType) =>
  String(aliasType || "").trim().toLowerCase() === "brand";

const companySelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "33px",
    borderColor: "var(--ct-input-border-color)",
    "&:hover": { borderColor: "var(--ct-input-border-color)" },
  }),
  menu: (base) => ({ ...base, marginTop: 0, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const fetchCompanyPage = async (searchTerm, pageNumber) => {
  const res = await rpost(`${DEV_URL}/api/company/getall`, {
    PageNumber: pageNumber,
    PageSize: COMPANY_PAGE_SIZE,
    SearchTerm: searchTerm || null,
    CompanyID: null,
    ClientID: null,
    LicenseID: null,
    orderBy: null,
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    LicenseStatus: null,
  });
  const json = res?.data;
  let items = [];
  if (json?.company && Array.isArray(json.company)) {
    items = json.company;
  } else if (json?.Company && Array.isArray(json.Company)) {
    items = json.Company;
  } else if (json?.data?.company && Array.isArray(json.data.company)) {
    items = json.data.company;
  } else if (json?.data?.Company && Array.isArray(json.data.Company)) {
    items = json.data.Company;
  } else if (Array.isArray(json)) {
    items = json;
  } else if (json?.data && Array.isArray(json.data)) {
    items = json.data;
  }
  return items
    .map((c) => ({
      value: c.id ?? c.Id,
      label: c.legalName || c.LegalName || c.name || c.companyName || "",
    }))
    .filter((option) => option.value && option.label);
};

export const AliasesData = ({
  companyId,
  onSetDisplayName,
  onRemoveAlias,
  onSwapParent,
  priviladge,
  aliases,
  onRefreshAliases,
  onRefreshDomains,
}) => {
  const isPremium = useGlobalPremiumFlag();
  const [addressContactData, setAddressContactData] = useState();
  const [relatedCompaniesData, setRelatedCompaniesData] = useState([]);
  const relatedCompaniesRef = useRef([]);
  const [loadingRelatedCompanies, setLoadingRelatedCompanies] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showAddAliasModal, setShowAddAliasModal] = useState(false);
  const [newAliasName, setNewAliasName] = useState("");
  const [newAliasType, setNewAliasType] = useState("FKA");
  const [addingAlias, setAddingAlias] = useState(false);
  const [removingAlias, setRemovingAlias] = useState(false);
  const [transferringAlias, setTransferringAlias] = useState(false);
  const [transferAliasModal, setTransferAliasModal] = useState({
    show: false,
    row: null,
  });
  const [selectedAliasTransferCompany, setSelectedAliasTransferCompany] =
    useState(null);
  const [aliasTransferCompanyOptions, setAliasTransferCompanyOptions] =
    useState([]);
  const [aliasTransferCompanyLoading, setAliasTransferCompanyLoading] =
    useState(false);
  const [aliasTransferError, setAliasTransferError] = useState("");
  const [aliasMappingConfirmModal, setAliasMappingConfirmModal] = useState({
    show: false,
    row: null,
  });
  const [aliasDeleteConfirmModal, setAliasDeleteConfirmModal] = useState({
    show: false,
    row: null,
  });
  const [duplicateAliasModal, setDuplicateAliasModal] = useState({
    show: false,
    aliasName: "",
    companies: [],
  });
  const [selectedChildCompanies, setSelectedChildCompanies] = useState([]);
  const [childError, setChildError] = useState("");
  const debounceTimerRef = useRef(null);
  const aliasTransferCompanyPageRef = useRef(1);
  const aliasTransferCompanySearchRef = useRef("");
  const aliasTransferCompanyHasMoreRef = useRef(true);
  const aliasTransferCompanySearchTimerRef = useRef(null);
  const aliasTransferCompanyLoadOptionsTimerRef = useRef(null);
  const aliasTransferCompanyLoadOptionsResolveRef = useRef(null);
  const snackbar = useShowSnackbar();
  const [brandPrefillAlias, setBrandPrefillAlias] = useState(null);

  const { isSystemAdmin } = useAuth();
  const addressColumns = [
    {
      header: "Type",
      accessorKey: "aliasType",
      cell: ({ row }) => (
        <span
          title={getAliasTypeTitle(row.original.aliasType)}
          className={`px-2 font-monospace badge bg-${getAliasTypeBadgeVariant(row.original.aliasType)}`}
        >
          {row.original.aliasType}
        </span>
      ),
      enableSorting: false,
    },
    {
      header: "Alias Name",
      accessorKey: "aliasName",
      cell: ({ row }) => (
        <BlurSpan isBlur={!isPremium}>
          {row.original.aliasName}
        </BlurSpan>
      ),
    },
  ];
  if (isSystemAdmin) {
    addressColumns.push({
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ThreeDotMenu className="text-center">
          <ConfirmButton
            message={
              <>
                Are you sure you want to change the display name to{" "}
                <strong>"{row.original.aliasName || "this alias"}"</strong>? This action
                cannot be undone.
              </>
            }
            confirmLabel="Proceed"
            cancelLabel="Cancel"
            showCancelButton
            onConfirm={(e) => {
              onSetDisplayName(row.original.id, row.original.aliasName);
            }}
            label={<small>Set Primary</small>}
            variant="dropdownItem"
          />
          <DropdownItem onClick={() => openTransferAliasModal(row.original)}>
            <small>Transfer Alias</small>
          </DropdownItem>
          {!isBrandAlias(row.original.aliasType) && (
            <DropdownItem onClick={() => setBrandPrefillAlias(row.original)}>
              <small>Convert to Brand</small>
            </DropdownItem>
          )}
          <DropdownItem
            disabled={removingAlias}
            onClick={() => handleRemoveAliasRequest(row.original)}
          >
            <small>Remove</small>
          </DropdownItem>
        </ThreeDotMenu>
      ),
      enableSorting: false,
      enableHiding: false,
    });
  }
  const relatedCompaniesColumns = [
    {
      header: "Legal Name",
      accessorKey: "legalName",
      cell: ({ row }) =>
        isPremium ? (
          <Link
            to={`/companies/${row.original.id}`}
            style={{ textDecoration: "none", color: "inherit" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.original.legalName === null || row.original.legalName === undefined
              ? ""
              : row.original.legalName}
          </Link>
        ) : (
          <BlurSpan isBlur>
            <span>
              {row.original.legalName === null || row.original.legalName === undefined
                ? ""
                : row.original.legalName}
            </span>
          </BlurSpan>
        ),
    },
    {
      header: "Domain",
      accessorKey: "emailDomain",
      cell: ({ row }) => (
        <BlurSpan isBlur={!isPremium}>
          <span>
            {row.original.emailDomain === null || row.original.emailDomain === undefined
              ? ""
              : row.original.emailDomain}
          </span>
        </BlurSpan>
      ),
      enableSorting: false,
    },
    // {
    //     name: "Corporate Type",
    //     selector: (row) => (row.corporateType === undefined || row.corporateType == null || row.corporateType==='')?'N/A':row.corporateType,
    //     grow: 1,
    //     sortable: true,
    // },
    {
      header: "Total License",
      accessorKey: "licenseCount",
    },
    {
      header: "Total Contacts",
      accessorKey: "contactCount",
    },
  ];
  if (isSystemAdmin) {
    relatedCompaniesColumns.push({
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ThreeDotMenu className="text-center">
          <ConfirmButton
            message="Are you sure you want to change parent? This action cannot be undone."
            confirmLabel="Proceed"
            cancelLabel="Cancel"
            showCancelButton
            onConfirm={async () => {
              onSwapParent(row.original.id);
            }}
            label={<small>Set Parent</small>}
            variant="dropdownItem"
          />
          <ConfirmButton
            title="Remove child company"
            message={
              <>
                Are you sure you want to remove <strong>{row.original.legalName}</strong>{" "}
                as a child of this company? This action cannot be undone.
              </>
            }
            confirmLabel="Remove"
            cancelLabel="Cancel"
            showCancelButton
            onConfirm={() => handleRemoveChild(row.original.id)}
            label={<small className="text-danger">Remove Child</small>}
            variant="dropdownItem"
            buttonVariant="outline-danger"
            confirmVariant="danger"
          />
        </ThreeDotMenu>
      ),
      enableSorting: false,
      enableHiding: false,
    });
  }

  // Use aliases prop if provided
  useEffect(() => {
    if (aliases && Array.isArray(aliases)) {
      const aliasList = aliases.map((c) => {
        return { ...c, aliasName: c?.aliasName?.toLowerCase() };
      });
      setAddressContactData(aliasList);
    }
  }, [aliases]);

  // Fetch related companies
  useEffect(() => {
    const fetchRelatedCompanies = async () => {
      setLoadingRelatedCompanies(true);
      try {
        const relatedCompaniesResponse = await rpost(
          `${DEV_URL}/api/Company/GetRelatedCompanies`,
          {
            PageNumber: 1,
            PageSize: 100,
            SearchTerm: "",
            companyId: companyId,
          }
        );
        const relatedCompaniesList = relatedCompaniesResponse?.data?.map(
          (c) => {
            return { ...c, legalName: c?.legalName?.toLowerCase() };
          }
        );
        setRelatedCompaniesData(relatedCompaniesList ?? []);
        relatedCompaniesRef.current = relatedCompaniesList ?? [];
      } catch (error) {
        console.error("Error fetching related companies:", error);
        setRelatedCompaniesData([]);
        relatedCompaniesRef.current = [];
      } finally {
        setLoadingRelatedCompanies(false);
      }
    };
    if (companyId) {
      fetchRelatedCompanies();
    }
  }, [companyId]);

  const loadChildOptions = useCallback(
    async (inputValue) => {
      if (!inputValue) {
        return [];
      }

      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Return a promise that resolves after debounce delay
      return new Promise((resolve) => {
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const response = await rpost(
              `${DEV_URL}/api/company/SearchCompany`,
              {
                Search: inputValue,
                Limit: 50,
              }
            );

            if (response.data && response.data.data) {
              // Filter out companies that are already selected
              const selectedIds = selectedChildCompanies.map((c) => c.value.id);
              let options = response.data.data
                .filter(
                  (c) => !selectedIds.includes(c.id) && c.id !== companyId
                )
                .map((c) => ({
                  value: c,
                  label: c.legalName,
                }));

              if (relatedCompaniesRef.current.length > 0) {
                options = options.filter(
                  (c) =>
                    !relatedCompaniesRef.current.some(
                      (ref) => ref.id === c.value.id
                    )
                );
              }
              resolve(options);
            } else {
              resolve([]);
            }
          } catch (error) {
            console.error(
              "Error searching companies for child selection",
              error
            );
            resolve([]);
          }
        }, 300); // 300ms debounce delay
      });
    },
    [selectedChildCompanies, companyId]
  );

  const handleChildChange = (selectedOptions) => {
    setSelectedChildCompanies(selectedOptions || []);
    setChildError("");
  };

  const handleRemoveChild = async (companyIdToRemove) => {
    try {
      const response = await updateCompanyHierarchy([
        {
          CompanyId: companyIdToRemove,
          NewParentId: null,
          ActionType: "removed_child", // Explicitly indicate this is removing a child (from parent's perspective)
        },
      ]);

      if (response?.data?.success) {
        // Refresh page to get updated data
        window.location.reload();
      } else {
        snackbar(
          response?.data?.message || "Failed to remove child company",
          "error"
        );
      }
    } catch (error) {
      console.error("Error removing child:", error);
      snackbar("An error occurred while removing child company", "error");
    }
  };

  const handleAddChildren = async () => {
    if (!selectedChildCompanies || selectedChildCompanies.length === 0) {
      setChildError("Please select at least one child company.");
      return;
    }

    // Validate: Cannot select self
    const hasSelf = selectedChildCompanies.some(
      (c) => c.value.id === companyId
    );
    if (hasSelf) {
      setChildError("A company cannot be its own child.");
      return;
    }

    try {
      setChildError("");
      const response = await updateCompanyHierarchy(
        selectedChildCompanies.map((c) => ({
          CompanyId: c.value.id,
          NewParentId: companyId, // Current company becomes their parent
          ActionType: "added_child", // Parent is adding a child (from parent's perspective)
        }))
      );

      if (response?.data?.success) {
        snackbar("Child companies added successfully", "success");
        setShowAddChildModal(false);
        setSelectedChildCompanies([]);
        // Refresh page to get updated data
        window.location.reload();
      } else {
        const errorMessage =
          response?.data?.message || "Failed to add child companies";
        setChildError(errorMessage);
        snackbar(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error adding child companies:", error);
      const errorMessage = "An error occurred while adding child companies";
      setChildError(errorMessage);
      snackbar(errorMessage, "error");
    }
  };

  const closeAddChildModal = () => {
    // Clear any pending debounce timer when closing modal
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setShowAddChildModal(false);
    setSelectedChildCompanies([]);
    setChildError("");
  };

  const getAliasId = (aliasOrId) =>
    typeof aliasOrId === "object"
      ? aliasOrId?.id ?? aliasOrId?.Id
      : aliasOrId;

  const getAliasCompanyId = (alias) =>
    alias?.companyId ?? alias?.CompanyId ?? companyId;

  const getAliasName = (alias) =>
    alias?.aliasName ?? alias?.AliasName ?? "this alias";

  const filterAliasTransferCompanies = useCallback(
    (options, aliasRow) => {
      const currentCompanyId = getAliasCompanyId(aliasRow);
      return options.filter((option) => option.value !== currentCompanyId);
    },
    [companyId]
  );

  const loadInitialAliasTransferCompanies = useCallback(
    async (aliasRow) => {
      setAliasTransferCompanyLoading(true);
      try {
        const options = await fetchCompanyPage("", 1);
        aliasTransferCompanyPageRef.current = 1;
        aliasTransferCompanySearchRef.current = "";
        aliasTransferCompanyHasMoreRef.current =
          options.length >= COMPANY_PAGE_SIZE;
        setAliasTransferCompanyOptions(
          filterAliasTransferCompanies(options, aliasRow)
        );
      } catch (error) {
        console.error("Failed to fetch companies for alias transfer", error);
        setAliasTransferCompanyOptions([]);
      } finally {
        setAliasTransferCompanyLoading(false);
      }
    },
    [filterAliasTransferCompanies]
  );

  const openTransferAliasModal = (row) => {
    setSelectedAliasTransferCompany(null);
    setAliasTransferCompanyOptions([]);
    setAliasTransferError("");
    setTransferAliasModal({ show: true, row });
    loadInitialAliasTransferCompanies(row);
  };

  const closeTransferAliasModal = () => {
    if (aliasTransferCompanySearchTimerRef.current) {
      clearTimeout(aliasTransferCompanySearchTimerRef.current);
      aliasTransferCompanySearchTimerRef.current = null;
    }
    if (aliasTransferCompanyLoadOptionsTimerRef.current) {
      clearTimeout(aliasTransferCompanyLoadOptionsTimerRef.current);
      aliasTransferCompanyLoadOptionsTimerRef.current = null;
    }
    if (aliasTransferCompanyLoadOptionsResolveRef.current) {
      aliasTransferCompanyLoadOptionsResolveRef.current([]);
      aliasTransferCompanyLoadOptionsResolveRef.current = null;
    }
    setTransferAliasModal({ show: false, row: null });
    setSelectedAliasTransferCompany(null);
    setAliasTransferCompanyOptions([]);
    setAliasTransferError("");
  };

  const handleAliasTransferInputChange = useCallback(
    (inputValue, actionMeta, aliasRowOverride = null) => {
      if (actionMeta?.action !== "input-change") {
        return inputValue;
      }

      if (aliasTransferCompanySearchTimerRef.current) {
        clearTimeout(aliasTransferCompanySearchTimerRef.current);
      }

      aliasTransferCompanySearchTimerRef.current = setTimeout(async () => {
        const activeAlias =
          aliasRowOverride ||
          transferAliasModal.row ||
          aliasMappingConfirmModal.row;
        aliasTransferCompanySearchRef.current = inputValue;
        aliasTransferCompanyPageRef.current = 1;
        aliasTransferCompanyHasMoreRef.current = true;
        setAliasTransferCompanyLoading(true);

        try {
          const options = await fetchCompanyPage(inputValue, 1);
          aliasTransferCompanyHasMoreRef.current =
            options.length >= COMPANY_PAGE_SIZE;
          setAliasTransferCompanyOptions(
            filterAliasTransferCompanies(options, activeAlias)
          );
        } catch (error) {
          console.error("Failed to search companies for alias transfer", error);
          setAliasTransferCompanyOptions([]);
        } finally {
          setAliasTransferCompanyLoading(false);
        }
      }, 400);

      return inputValue;
    },
    [
      transferAliasModal.row,
      aliasMappingConfirmModal.row,
      filterAliasTransferCompanies,
    ]
  );

  const handleAliasTransferScrollToBottom = useCallback(
    async (aliasRowOverride = null) => {
      if (
        !aliasTransferCompanyHasMoreRef.current ||
        aliasTransferCompanyLoading
      ) {
        return;
      }

      const activeAlias =
        aliasRowOverride ||
        transferAliasModal.row ||
        aliasMappingConfirmModal.row;
      setAliasTransferCompanyLoading(true);

      try {
        const nextPage = aliasTransferCompanyPageRef.current + 1;
        const options = await fetchCompanyPage(
          aliasTransferCompanySearchRef.current,
          nextPage
        );
        aliasTransferCompanyPageRef.current = nextPage;
        aliasTransferCompanyHasMoreRef.current =
          options.length >= COMPANY_PAGE_SIZE;
        const filteredOptions = filterAliasTransferCompanies(
          options,
          activeAlias
        );

        setAliasTransferCompanyOptions((previousOptions) => {
          const existingValues = new Set(
            previousOptions.map((option) => option.value)
          );
          return [
            ...previousOptions,
            ...filteredOptions.filter(
              (option) => !existingValues.has(option.value)
            ),
          ];
        });
      } catch (error) {
        console.error("Failed to load more companies for alias transfer", error);
      } finally {
        setAliasTransferCompanyLoading(false);
      }
    },
    [
      aliasTransferCompanyLoading,
      transferAliasModal.row,
      aliasMappingConfirmModal.row,
      filterAliasTransferCompanies,
    ]
  );

  const loadAliasTransferCompanyOptions = useCallback(
    (inputValue, aliasRowOverride = null) => {
      if (aliasTransferCompanyLoadOptionsTimerRef.current) {
        clearTimeout(aliasTransferCompanyLoadOptionsTimerRef.current);
      }
      if (aliasTransferCompanyLoadOptionsResolveRef.current) {
        aliasTransferCompanyLoadOptionsResolveRef.current([]);
        aliasTransferCompanyLoadOptionsResolveRef.current = null;
      }

      return new Promise((resolve) => {
        aliasTransferCompanyLoadOptionsResolveRef.current = resolve;
        aliasTransferCompanyLoadOptionsTimerRef.current = setTimeout(
          async () => {
            aliasTransferCompanyLoadOptionsTimerRef.current = null;
            aliasTransferCompanyLoadOptionsResolveRef.current = null;

            try {
              const options = await fetchCompanyPage(inputValue, 1);
              const activeAlias =
                aliasRowOverride ||
                transferAliasModal.row ||
                aliasMappingConfirmModal.row;
              resolve(filterAliasTransferCompanies(options, activeAlias));
            } catch (error) {
              console.error(
                "Failed to fetch companies for alias transfer",
                error
              );
              resolve([]);
            }
          },
          400
        );
      });
    },
    [
      transferAliasModal.row,
      aliasMappingConfirmModal.row,
      filterAliasTransferCompanies,
    ]
  );

  const handleTransferAlias = async (aliasRow, onSuccessClose) => {
    const aliasId = getAliasId(aliasRow);
    const targetCompanyId = selectedAliasTransferCompany?.value;

    if (!aliasId) {
      setAliasTransferError("Unable to transfer alias");
      return;
    }

    if (!targetCompanyId) {
      setAliasTransferError("Please select target company");
      return;
    }

    try {
      setTransferringAlias(true);
      setAliasTransferError("");

      const response = await rpost(`${DEV_URL}/api/company/Alias/Transfer`, {
        AliasId: aliasId,
        CompanyId: companyId,
        TargetCompanyId: targetCompanyId,
        ExistingCompanyId: getAliasCompanyId(aliasRow),
      });

      if (
        response?.status >= 200 &&
        response?.status < 300 &&
        response?.data?.success !== false &&
        response?.data?.Success !== false
      ) {
        snackbar("Alias transferred successfully", "success");
        onSuccessClose();
        if (onRefreshAliases) {
          await onRefreshAliases();
        } else {
          window.location.reload();
        }
      } else {
        setAliasTransferError(
          response?.data?.message ||
          response?.data?.Message ||
          "Failed to transfer alias"
        );
      }
    } catch (error) {
      console.error("Failed to transfer alias:", error);
      setAliasTransferError("Failed to transfer alias");
    } finally {
      setTransferringAlias(false);
    }
  };

  const getHasAliasApplicationMapping = async (aliasId) => {
    const response = await rpost(
      `${DEV_URL}/api/company/AssetApplicationMapping`,
      {
        AssetId: aliasId,
        AssetType: "Alias",
      }
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify alias asset mapping",
        "error"
      );
      return null;
    }

    const resultData =
      response?.data?.data ?? response?.data?.Data ?? response?.data;
    const mappingResult = Array.isArray(resultData)
      ? resultData[0]
      : resultData;
    const hasApplicationMapping =
      mappingResult?.hasApplicationMapping ??
      mappingResult?.HasApplicationMapping ??
      false;

    return (
      hasApplicationMapping === true ||
      hasApplicationMapping === 1 ||
      String(hasApplicationMapping).toLowerCase() === "true"
    );
  };

  const closeAliasMappingConfirmModal = () => {
    if (aliasTransferCompanySearchTimerRef.current) {
      clearTimeout(aliasTransferCompanySearchTimerRef.current);
      aliasTransferCompanySearchTimerRef.current = null;
    }
    if (aliasTransferCompanyLoadOptionsTimerRef.current) {
      clearTimeout(aliasTransferCompanyLoadOptionsTimerRef.current);
      aliasTransferCompanyLoadOptionsTimerRef.current = null;
    }
    if (aliasTransferCompanyLoadOptionsResolveRef.current) {
      aliasTransferCompanyLoadOptionsResolveRef.current([]);
      aliasTransferCompanyLoadOptionsResolveRef.current = null;
    }
    setAliasMappingConfirmModal({ show: false, row: null });
    setSelectedAliasTransferCompany(null);
    setAliasTransferCompanyOptions([]);
    setAliasTransferError("");
  };

  const closeAliasDeleteConfirmModal = () => {
    setAliasDeleteConfirmModal({ show: false, row: null });
  };

  const handleRemoveAliasRequest = async (aliasOrId) => {
    const aliasId = getAliasId(aliasOrId);
    if (!aliasId) {
      snackbar("Unable to remove alias", "error");
      return;
    }

    try {
      setRemovingAlias(true);
      const hasApplicationMapping = await getHasAliasApplicationMapping(
        aliasId
      );

      if (hasApplicationMapping === null) {
        return;
      }

      if (hasApplicationMapping) {
        setSelectedAliasTransferCompany(null);
        setAliasTransferCompanyOptions([]);
        setAliasTransferError("");
        setAliasMappingConfirmModal({ show: true, row: aliasOrId });
        loadInitialAliasTransferCompanies(aliasOrId);
        return;
      }

      setAliasDeleteConfirmModal({ show: true, row: aliasOrId });
    } catch (error) {
      console.error("Error checking alias asset mapping:", error);
      snackbar("An error occurred while checking alias asset mapping", "error");
    } finally {
      setRemovingAlias(false);
    }
  };

  const handleRemoveAlias = async (
    aliasOrId,
    skipApplicationMappingCheck = false
  ) => {
    const aliasId = getAliasId(aliasOrId);
    if (!aliasId) {
      snackbar("Unable to remove alias", "error");
      return;
    }

    try {
      setRemovingAlias(true);
      if (!skipApplicationMappingCheck) {
        const hasApplicationMapping = await getHasAliasApplicationMapping(
          aliasId
        );
        if (hasApplicationMapping === null) {
          return;
        }
        if (hasApplicationMapping) {
          setSelectedAliasTransferCompany(null);
          setAliasTransferCompanyOptions([]);
          setAliasTransferError("");
          setAliasMappingConfirmModal({ show: true, row: aliasOrId });
          loadInitialAliasTransferCompanies(aliasOrId);
          return;
        }
      }

      await onRemoveAlias(aliasId);
    } catch (error) {
      console.error("Error removing alias:", error);
      snackbar("An error occurred while removing alias", "error");
    } finally {
      setRemovingAlias(false);
    }
  };

  const getDuplicateAliasConflicts = (response, aliasName) => {
    const data = response?.data ?? response;
    const conflicts =
      data?.conflicts ??
      data?.Conflicts ??
      data?.data?.conflicts ??
      data?.data?.Conflicts ??
      [];

    if (!Array.isArray(conflicts)) {
      return [];
    }

    return conflicts
      .map((item) => ({
        companyId: item.companyId || item.CompanyId,
        companyName:
          item.companyName ||
          item.CompanyName ||
          item.legalName ||
          item.LegalName ||
          "Company",
        companyStatus: item.companyStatus || item.CompanyStatus || "",
        aliasName: item.aliasName || item.AliasName || aliasName,
        aliasType: item.aliasType || item.AliasType || "",
        enerexCompanyIdentifier:
          item.enerexCompanyIdentifier || item.EnerexCompanyIdentifier || "",
      }))
      .filter((item) => item.companyId);
  };

  const openDuplicateAliasModal = (response, aliasName) => {
    const companies = getDuplicateAliasConflicts(response, aliasName);
    if (companies.length === 0) {
      return false;
    }

    setDuplicateAliasModal({
      show: true,
      aliasName,
      companies,
    });
    return true;
  };

  const closeDuplicateAliasModal = () => {
    setDuplicateAliasModal({
      show: false,
      aliasName: "",
      companies: [],
    });
  };

  const handleAddAlias = async () => {
    if (!newAliasName.trim()) {
      snackbar("Please enter an alias name", "error");
      return;
    }
    setAddingAlias(true);
    try {
      const response = await rpost(
        `${DEV_URL}/api/company/${companyId}/Alias`,
        {
          AliasName: newAliasName.trim(),
          AliasType: newAliasType,
          AssociatedCompanyID: companyId,
        }
      );
      if (response?.data?.success) {
        snackbar(
          response.data.message || "Alias added successfully",
          "success"
        );
        setShowAddAliasModal(false);
        setNewAliasName("");
        setNewAliasType("FKA");
        if (onRefreshAliases) await onRefreshAliases();
      } else {
        if (openDuplicateAliasModal(response, newAliasName.trim())) {
          return;
        }
        snackbar(response?.data?.message || "Failed to add alias", "error");
      }
    } catch (error) {
      console.error("Error adding alias:", error);
      if (openDuplicateAliasModal(error?.response, newAliasName.trim())) {
        return;
      }
      snackbar(
        error?.response?.data?.message ||
        "An error occurred while adding alias",
        "error"
      );
    } finally {
      setAddingAlias(false);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (aliasTransferCompanySearchTimerRef.current) {
        clearTimeout(aliasTransferCompanySearchTimerRef.current);
        aliasTransferCompanySearchTimerRef.current = null;
      }
      if (aliasTransferCompanyLoadOptionsTimerRef.current) {
        clearTimeout(aliasTransferCompanyLoadOptionsTimerRef.current);
        aliasTransferCompanyLoadOptionsTimerRef.current = null;
      }
      if (aliasTransferCompanyLoadOptionsResolveRef.current) {
        aliasTransferCompanyLoadOptionsResolveRef.current([]);
        aliasTransferCompanyLoadOptionsResolveRef.current = null;
      }
    };
  }, []);

  return (
    <span>
      <Div className="mt-4 d-flex justify-content-between align-items-center">
        <PageTitle title="Aliases and Related Parties"></PageTitle>
        <Div className="d-flex align-items-center gap-2">
          {isSystemAdmin && (
            <Button
              variant="primary"
              size="sm"
              className="fs-10"
              style={{
                padding: "2px 6px",
                fontSize: "10px",
                lineHeight: "1.2",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
              }}
              onClick={() => {
                setShowAddAliasModal(true);
                setNewAliasName("");
                setNewAliasType("FKA");
              }}
            >
              <PlusIcon style={{ fontSize: "9px" }} />
              <small>Add Alias</small>
            </Button>
          )}
        </Div>
      </Div>
      <Card>
        <Card.Body>
          <Row>
            <Col sm="12">
              {!aliases ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "150px" }}
                >
                  <img src={loader} height={"50px"} />
                </div>
              ) : (
                <Table
                  columns={addressColumns}
                  data={addressContactData || []}
                  loading={!aliases}
                  enableSearch={false}
                  enablePagination={false}
                  enableHideColumns={false}
                  enableSorting
                  enableFilters={false}
                  enableSelection={false}
                  enablePaginateAll={false}
                  enableColumnVisibilityStorage={false}
                  enableGlobalFilterStorage={false}
                  enableUrlPagination={false}
                  noDataMessage="No aliases found"
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {
        isSystemAdmin && (
          <BrandsData
            companyId={companyId}
            prefillAlias={brandPrefillAlias}
            onConvertHandled={() => setBrandPrefillAlias(null)}
            onRefreshAliases={onRefreshAliases}
            onRefreshDomains={onRefreshDomains}
          />
        )
      }

      <Div className="mt-4 d-flex justify-content-between align-items-center">
        <PageTitle title="Child Companies"></PageTitle>
        <Div className="d-flex align-items-center gap-2">
          {isSystemAdmin && (
            <Button
              variant="primary"
              size="sm"
              className="fs-10"
              style={{
                padding: "2px 6px",
                fontSize: "10px",
                lineHeight: "1.2",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
              }}
              onClick={() => {
                setShowAddChildModal(true);
                setSelectedChildCompanies([]);
                setChildError("");
              }}
            >
              <PlusIcon style={{ fontSize: "9px" }} />
              <small>Add Child</small>
            </Button>
          )}
        </Div>
      </Div>
      <Card>
        <Card.Body>
          <Row>
            <Col sm="12">
              {loadingRelatedCompanies ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "150px" }}
                >
                  <img src={loader} height={"50px"} />
                </div>
              ) : (
                <Table
                  columns={relatedCompaniesColumns}
                  data={relatedCompaniesData}
                  loading={loadingRelatedCompanies}
                  enableSearch={false}
                  enablePagination={false}
                  enableHideColumns={false}
                  enableSorting
                  enableFilters={false}
                  enableSelection={false}
                  enablePaginateAll={false}
                  enableColumnVisibilityStorage={false}
                  enableGlobalFilterStorage={false}
                  enableUrlPagination={false}
                  noDataMessage="No child companies found"
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <AddChildModal
        show={showAddChildModal}
        selectedChildCompanies={selectedChildCompanies}
        childError={childError}
        onClose={closeAddChildModal}
        onChildChange={handleChildChange}
        onAdd={handleAddChildren}
        loadChildOptions={loadChildOptions}
      />
      <ConfirmationModal
        dialogClassName="modal-md"
        show={transferAliasModal.show}
        onHide={closeTransferAliasModal}
        centered
        title="Transfer Alias"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={transferringAlias}
        onConfirm={() =>
          handleTransferAlias(transferAliasModal.row, closeTransferAliasModal)
        }
      >
        {aliasTransferError ? (
          <Alert variant="danger">{aliasTransferError}</Alert>
        ) : null}
        <p>
          Transfer alias <strong>{getAliasName(transferAliasModal.row)}</strong>{" "}
          to a different company.
        </p>
        <Form.Group>
          <Form.Label>Select Target Company</Form.Label>
          <Select
            options={aliasTransferCompanyOptions}
            onMenuOpen={() => {
              if (aliasTransferCompanyOptions.length === 0) {
                loadInitialAliasTransferCompanies(transferAliasModal.row);
              }
            }}
            onMenuScrollToBottom={() =>
              handleAliasTransferScrollToBottom(transferAliasModal.row)
            }
            onInputChange={(inputValue, actionMeta) =>
              handleAliasTransferInputChange(
                inputValue,
                actionMeta,
                transferAliasModal.row
              )
            }
            value={selectedAliasTransferCompany}
            onChange={(value) => {
              setSelectedAliasTransferCompany(value);
              setAliasTransferError("");
            }}
            placeholder="Search company..."
            isClearable
            isSearchable
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            isLoading={aliasTransferCompanyLoading}
            maxMenuHeight={250}
          />
        </Form.Group>
      </ConfirmationModal>
      <ConfirmationModal
        dialogClassName="modal-md"
        show={showAddAliasModal}
        onHide={() => setShowAddAliasModal(false)}
        centered
        title="Add Alias"
        confirmButtonLabel={addingAlias ? "Adding..." : "Add Alias"}
        confirmButtonVariant="primary"
        isLoading={addingAlias}
        onConfirm={handleAddAlias}
      >
        <FormInput
          type="text"
          label="Alias Name"
          containerClass="mb-3"
          placeholder="Enter alias name"
          value={newAliasName}
          onChange={(e) => setNewAliasName(e.target.value)}
        />
        <Form.Group className="mb-3">
          <Form.Label>Alias Type</Form.Label>
          <Select
            options={ALIAS_TYPE_OPTIONS}
            value={findOptionByValue(ALIAS_TYPE_OPTIONS, newAliasType)}
            onChange={(option) => setNewAliasType(option?.value || "FKA")}
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
      </ConfirmationModal>
      <ConfirmationModal
        dialogClassName="modal-md"
        show={duplicateAliasModal.show}
        onHide={closeDuplicateAliasModal}
        centered
        title="Duplicate Alias"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        onConfirm={closeDuplicateAliasModal}
      >
        <p className="mb-3">
          <strong>{duplicateAliasModal.aliasName}</strong> is already present
          for the following{" "}
          {duplicateAliasModal.companies.length === 1
            ? "company"
            : "companies"}
          .
        </p>
        <div className="d-flex flex-column gap-2">
          {duplicateAliasModal.companies.map((item) => (
            <div
              key={`${item.companyId}-${item.aliasName}-${item.aliasType}`}
              className="border rounded p-2"
            >
              <a
                href={`/companies/${item.companyId}`}
                target="_blank"
                rel="noreferrer"
              >
                {item.companyName}
              </a>
              {item.companyStatus ? (
                <small className="text-muted d-block">
                  Status: {item.companyStatus}
                </small>
              ) : null}
              {item.aliasType ? (
                <small className="text-muted d-block">
                  Alias Type: {item.aliasType}
                </small>
              ) : null}
              {item.enerexCompanyIdentifier ? (
                <small className="text-muted d-block">
                  Enerex Company Id: {item.enerexCompanyIdentifier}
                </small>
              ) : null}
            </div>
          ))}
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        dialogClassName="modal-md"
        show={aliasDeleteConfirmModal.show}
        onHide={closeAliasDeleteConfirmModal}
        centered
        title="Remove Alias"
        confirmButtonLabel="Delete"
        confirmButtonVariant="danger"
        isLoading={removingAlias}
        onConfirm={async () => {
          await handleRemoveAlias(aliasDeleteConfirmModal.row, true);
          closeAliasDeleteConfirmModal();
        }}
      >
        <p>
          Are you sure you want to remove{" "}
          <strong>{getAliasName(aliasDeleteConfirmModal.row)}</strong>?
        </p>
      </ConfirmationModal>
      <ConfirmationModal
        dialogClassName="modal-md"
        show={aliasMappingConfirmModal.show}
        onHide={closeAliasMappingConfirmModal}
        centered
        title="Transfer Alias"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={transferringAlias}
        onConfirm={() =>
          handleTransferAlias(
            aliasMappingConfirmModal.row,
            closeAliasMappingConfirmModal
          )
        }
      >
        <p>
          This alias is used in another product for the same company, so you
          cannot delete it. You can either transfer it to another company, or
          first delete/correct the product data, and then delete it.
        </p>
        {aliasTransferError ? (
          <Alert variant="danger">{aliasTransferError}</Alert>
        ) : null}
        <Form.Group>
          <Form.Label>Transfer alias to company</Form.Label>
          <AsyncSelect
            key={getAliasId(aliasMappingConfirmModal.row) || "alias-transfer"}
            cacheOptions
            defaultOptions
            loadOptions={(inputValue) =>
              loadAliasTransferCompanyOptions(
                inputValue,
                aliasMappingConfirmModal.row
              )
            }
            value={selectedAliasTransferCompany}
            onChange={(value) => {
              setSelectedAliasTransferCompany(value);
              setAliasTransferError("");
            }}
            placeholder="Search company..."
            isClearable
            isSearchable
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            maxMenuHeight={250}
          />
        </Form.Group>
      </ConfirmationModal>
    </span>
  );
};
